import {
    Component,
    Inject,
    OnInit,
    ViewChild,
    TemplateRef,
    ElementRef,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IPayment } from 'src/app/interfaces/payment.interface';
import {
    FormGroup,
    Validators,
    FormControl,
    ValidatorFn,
    AbstractControl,
} from '@angular/forms';
import { SelectOption } from 'src/app/interfaces/select';
import {
    Refund,
    RefundService,
    RefundType,
    Srvdep,
} from 'src/app/services/refund.service';
import { AlertService } from 'src/lib/ariadna/alert';
import moment from 'moment';

@Component({
    selector: 'refund-modal',
    templateUrl: 'refund.component.html',
    styleUrls: ['./refund.component.scss'],
})
export class RefundComponent implements OnInit {
    @ViewChild('closeModal') closeModalTemplate?: TemplateRef<any>;
    @ViewChild('datePicker') datePicker!: ElementRef<HTMLInputElement>;
    @ViewChild('dateInputRef') dateInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('dateDisplayInput')
    dateDisplayInput!: ElementRef<HTMLInputElement>;

    refundEnum = RefundType;
    closeModalRef?: MatDialogRef<any>;
    isLoading = false;
    refundCost = 0;
    selectedServices = new Set<Srvdep>();
    maxDate!: string;

    refundForm = new FormGroup<any>({});
    subform = new FormGroup<any>({});
    reasons: SelectOption[] = [];
    services?: Srvdep[];
    isMobile = false;
    formattedDate: string = ''; // ← добавлено

    constructor(
        private dialog: MatDialog,
        private refundService: RefundService,
        private alert: AlertService,
        public dialogRef: MatDialogRef<RefundComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IPayment,
    ) {
        // this.dialogRef.disableClose = true;
        this.isMobile = window.innerWidth <= 768;
    }

    ngOnInit() {
        this.init();
        this.isLoading = true;
        this.maxDate = moment(new Date()).format('YYYY-MM-DD');
        // Отключаем автоматическое закрытие при клике мимо
        this.dialogRef.disableClose = true;

        // Слушаем клик по бэкдропу (мимо окна)
        this.dialogRef.backdropClick().subscribe(() => {
            this.handleOutsideClick();
        });

        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth <= 768;
        });
    }

    ngOnDestroy() {
        window.removeEventListener('resize', () => {
            this.isMobile = window.innerWidth <= 768;
        });
    }

    private handleOutsideClick() {
        // Если пользователь ничего не вводил — закрываем без подтверждения
        if (!this.hasUserStartedFilling()) {
            this.close();
            return;
        }

        // Иначе — показываем модальное окно подтверждения
        this.openCloseModal();
    }

    async init() {
        try {
            await this.getServices();

            if (this.services) {
                this.initForm();
                this.getRefundReasons();
                this.services.forEach((srv) => this.selectedServices.add(srv));
                this.calcRefundCost();
            }
        } finally {
            this.isLoading = false;
        }
    }

    initForm() {
        // ... создание формы
        this.refundForm = new FormGroup<{}>({
            reason: new FormControl('', { validators: Validators.required }),
            comment: new FormControl(''),
            services: new FormControl(this.selectedServices, {
                validators: this.servicesValidator(),
            }),
            refundType: new FormControl(RefundType.cash, {
                validators: Validators.required,
            }),
            subform: new FormGroup({}),
        });

        // ← ДОБАВЬТЕ ЭТО:
        this.refundForm.valueChanges.subscribe(() => {
            if (this.refundForm && this.refundForm.pristine) {
                this.refundForm.markAsDirty();
            }
        });
    }

    async getRefundReasons() {
        try {
            const reasons = await this.refundService.fetchRefundReasons();

            if (reasons) {
                this.reasons = reasons.map(({ keyid, text }) => ({
                    text,
                    value: keyid,
                }));
            }
        } catch (error) {
            this.alert.warn('Ошибка при получении списка причин для возврата');
        }
    }

    async getServices() {
        try {
            const services = await this.refundService.fetchServices(
                this.data.pt_keyid,
            );
            this.services = services || [];
        } catch (error) {
            this.alert.warn('Ошибка при получении списка услуг');
        }
    }

    servicesValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            return control.value?.size
                ? null
                : { services: 'Не выбрана ни одна услуга' };
        };
    }

    get refundType(): RefundType {
        return this.refundForm.get('refundType')?.value;
    }

    set refundType(type: RefundType) {
        if (type === this.refundForm.get('refundType')?.value) return;

        this.refundForm.removeControl('subform');
        let subform = new FormGroup<any>({});

        if (type === RefundType.card) {
            subform = this.initCardSubformGroup();
        } else if (type === RefundType.requisits) {
            subform = this.initRequisitsSubformGroup();
        }

        this.subform = subform;
        this.refundForm.addControl('subform', subform);
        this.refundForm.get('refundType')?.setValue(type);

        // Сбросим дату при смене типа
        if (type !== RefundType.card) {
            this.formattedDate = '';
        } else {
            // Если уже была дата — обновим отображение
            const dateValue = this.subform.get('date')?.value;
            if (dateValue) {
                this.formatDateDisplay(dateValue);
            }
        }
    }

    onChangeService([value, checked]: [any, boolean]) {
        if (checked) {
            this.selectedServices.add(value);
        } else {
            this.selectedServices.delete(value);
        }
        this.calcRefundCost();
        this.refundForm.get('services')?.updateValueAndValidity();
    }

    calcRefundCost() {
        this.refundCost = Array.from(this.selectedServices).reduce(
            (sum, srv) => sum + +srv.amount,
            0,
        );
    }

    initCardSubformGroup() {
        const group = new FormGroup({
            number: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.minLength(4),
                    // Validators.maxLength(4),
                ],
            }),
            date: new FormControl('', {
                validators: [
                    Validators.required,
                    this.validateDateBeforeToday.bind(this),
                ],
            }),
        });

        // Если уже есть дата — обновим отображение
        const dateCtrl = group.get('date');
        if (dateCtrl?.value) {
            this.formatDateDisplay(dateCtrl.value);
        }

        return group;
    }

    validateDateBeforeToday(
        control: AbstractControl,
    ): { [key: string]: any } | null {
        const selectedDate = new Date(control.value);
        const currentDate = new Date();
        selectedDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        return selectedDate <= currentDate ? null : { invalidDate: true };
    }

    getMaxDate(): string {
        const today = new Date();
        return today.toISOString().split('T')[0]; // YYYY-MM-DD
    }

    initRequisitsSubformGroup() {
        return new FormGroup({
            bankName: new FormControl('', {
                validators: [Validators.required],
            }),
            inn: new FormControl('', {
                validators: [
                    Validators.required,
                    Validators.minLength(10),
                    Validators.maxLength(12),
                ],
            }),
            bik: new FormControl('', {
                validators: [Validators.required, Validators.minLength(9)],
            }),
            corr: new FormControl('', {
                validators: [Validators.required, Validators.minLength(20)],
            }),
            rsc: new FormControl('', {
                validators: [Validators.required, Validators.minLength(20)],
            }),
        });
    }

    // Форматируем дату для отображения: YYYY-MM-DD → DD.MM.YYYY
    // private formatDateDisplay(dateStr: string) {
    //     const [year, month, day] = dateStr.split('-');
    //     this.formattedDate = `${day}.${month}.${year}`;
    // }

    openDatepicker() {
        const picker = this.datePicker?.nativeElement;

        if (!picker) {
            console.warn('Скрытое поле даты (#datePicker) не найдено');
            return;
        }

        // Прокручиваем поле к верху экрана
        // picker.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Даём время на прокрутку
        setTimeout(() => {
            try {
                if (typeof picker.showPicker === 'function') {
                    picker.showPicker(); // showPicker() не возвращает Promise
                } else {
                    picker.click(); // fallback
                }
            } catch (error) {
                console.warn(
                    'showPicker() failed, falling back to click()',
                    error,
                );
                picker.click();
            }
        }, 100);
    }

    onDateInput(event: any) {
        const value = event.target.value; // YYYY-MM-DD
        if (value) {
            this.formatDateDisplay(value);
        } else {
            this.formattedDate = '';
        }
        // Обновим FormControl
        this.subform.get('date')?.setValue(value, { emitEvent: true });
    }

    public onInputRestrict(event: Event, maxLength: number): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        const digitsOnly = value.replace(/[^0-9]/g, '');
        const finalValue = digitsOnly.slice(0, maxLength);

        if (input.value !== finalValue) {
            input.value = finalValue;
        }

        const controlName = input.getAttribute('formControlName');
        if (controlName && this.subform.contains(controlName)) {
            const control = this.subform.get(controlName)!;
            if (control.value !== finalValue) {
                control.setValue(finalValue, { emitEvent: true });
                control.updateValueAndValidity({ emitEvent: true });
            }
        }
    }

    hasUserStartedFilling(): boolean {
        return !!(this.refundForm && !this.refundForm.pristine);
    }
    openCloseModal() {
        // Если пользователь ничего не вводил — закрываем без подтверждения
        if (!this.hasUserStartedFilling()) {
            this.close();
            return;
        }

        // Если есть изменения — показываем модальное окно подтверждения
        if (this.closeModalTemplate && this.services) {
            this.closeModalRef = this.dialog.open(this.closeModalTemplate, {
                panelClass: 'refund-modal',
            });
        } else {
            this.close();
        }
    }

    onCloseModal() {
        this.closeModalRef?.close();
    }

    close() {
        this.dialogRef.close();
        this.closeModalRef?.close();
    }

    async onSend(e: Event) {
        e.preventDefault();
        this.refundForm.markAllAsTouched();

        if (!this.refundForm.valid) return;

        try {
            const refund: Refund = {
                paymentId: this.data.pt_keyid,
                reasonId: this.f('reason')?.value?.value,
                note: this.f('comment')?.value || null,
                refundType: this.refundType,
                lastCardNum: this.subf('number')?.value,
                payDate: this.subf('date')?.value,
                bankName: this.subf('bankName')?.value,
                inn: this.subf('inn')?.value,
                bik: this.subf('bik')?.value,
                corrBill: this.subf('corr')?.value,
                accBill: this.subf('rsc')?.value,
                srvdepIds: this.getSrvdepIds(Array.from(this.selectedServices)),
            };

            const result = (await this.refundService.sendRefund(refund))[0];
            if (result?.error_code === 0 || result?.err_code === 0) {
                this.alert.success(
                    'Заявка на возврат успешно отправлена. Проверьте почту',
                    10000,
                );
                this.dialogRef.close();
            } else {
                throw new Error(
                    result?.error_text ||
                        result?.err_text ||
                        'Неизвестная ошибка',
                );
            }
        } catch (error: any) {
            this.alert.error(
                'Ошибка при отправке заявки: ' +
                    (error.message || 'Неизвестная ошибка'),
                10000,
            );
        }
    }

    getSrvdepIds(options: Srvdep[]): string {
        return options.map((item) => item.keyid).join(';');
    }

    f(controlName: string) {
        return this.refundForm.get(controlName);
    }

    subf(controlName: string) {
        return this.subform.get(controlName);
    }

    isFieldInvalid(fieldName: string): boolean {
        const control = this.subform.get(fieldName);
        if (!control) return false;

        if (fieldName === 'number') {
            return (
                control.touched &&
                (control.hasError('required') || control.hasError('minlength'))
            );
        }

        return control.invalid && (control.dirty || control.touched);
    }

    markAsTouched(fieldName: string): void {
        const control = this.subform.get(fieldName);
        if (control) {
            control.markAsTouched();

            // Специальная логика для поля 'number'
            if (fieldName === 'number') {
                const value = control.value?.toString() || '';
                const digits = value.replace(/[^0-9]/g, '');

                // Если после очистки — ровно 4 цифры → принудительно обновляем
                if (digits.length === 4) {
                    control.setValue(digits, { emitEvent: true });
                    control.updateValueAndValidity({ emitEvent: true });
                }
            }
        }

        // Сбрасываем previousValue при потере фокуса у даты
        if (fieldName === 'date') {
            this.previousValue = '';
        }
    }

    private filterDateInput(
        value: string,
        input?: HTMLInputElement,
        isDeleting: boolean = false,
    ): { value: string; cursor: number } {
        if (!value) return { value: '', cursor: 0 };

        let cursorPosition = input ? input.selectionStart : null;
        const oldValue = input ? input.value : '';

        let result = value.replace(/[^0-9.]/g, '');
        result = result.replace(/\.{2,}/g, '.');
        result = result.slice(0, 10);

        if (isDeleting && oldValue && cursorPosition !== null) {
            if (
                result.length === 5 &&
                (cursorPosition === 2 || cursorPosition === 5)
            ) {
                result =
                    result.substring(0, cursorPosition - 1) +
                    result.substring(cursorPosition);
                cursorPosition--;
            } else if (result.length === 2 && cursorPosition === 2) {
                result =
                    result.substring(0, cursorPosition - 1) +
                    result.substring(cursorPosition);
                cursorPosition--;
            }
        } else {
            // Если ввели одну цифру дня и она > 3 - автоматически добавляем 0
            if (result.length === 1 && !result.includes('.')) {
                const digit = parseInt(result[0], 10);
                if (digit > 3) {
                    result = '0' + result + '.';
                    if (cursorPosition === 1) {
                        cursorPosition = 3;
                    }
                }
            }
            // Если день заполнен (ДД) - добавляем точку и проверяем день
            else if (result.length === 2 && !result.includes('.')) {
                let day = parseInt(result, 10);
                if (day > 31) {
                    day = 31;
                    result = day.toString().padStart(2, '0');
                }
                result += '.';
                if (cursorPosition === 2) {
                    cursorPosition = 3;
                }
            }
            // Если после точки одна цифра месяца и она > 1 - автоматически добавляем 0
            else if (
                result.length === 4 &&
                result[2] === '.' &&
                result[3] !== '.' &&
                !result.includes('.', 4)
            ) {
                const monthDigit = parseInt(result[3], 10);
                if (monthDigit > 1) {
                    result = result.slice(0, 3) + '0' + result[3] + '.';
                    if (cursorPosition === 4) {
                        cursorPosition = 6;
                    }
                }
            }
            // Если день и месяц заполнены (ДД.ММ) - добавляем точку и проверяем месяц
            else if (
                result.length >= 5 &&
                result[2] === '.' &&
                !result.includes('.', 5)
            ) {
                // Извлекаем и проверяем день
                const dayPart = result.substring(0, 2);
                let day = parseInt(dayPart, 10);
                if (day > 31) {
                    day = 31;
                    result =
                        day.toString().padStart(2, '0') + result.substring(2);
                }

                // Извлекаем и проверяем месяц
                const monthPart = result.substring(3, 5);
                if (monthPart.length === 2) {
                    let month = parseInt(monthPart, 10);
                    if (month > 12) {
                        month = 12;
                        result =
                            result.substring(0, 3) +
                            month.toString().padStart(2, '0');
                    }
                }

                // Добавляем точку после месяца, если еще не добавлена
                if (result.length === 5) {
                    result += '.';
                    if (cursorPosition === 5) {
                        cursorPosition = 6;
                    }
                }
            }
        }

        if (input && cursorPosition !== null) {
            const addedDotsBefore = this.countDotsInRange(
                result,
                0,
                cursorPosition,
            );
            const removedDotsBefore = this.countDotsInRange(
                oldValue,
                0,
                cursorPosition,
            );
            const diff = addedDotsBefore - removedDotsBefore;
            cursorPosition += diff;
            cursorPosition = Math.max(
                0,
                Math.min(cursorPosition, result.length),
            );
        } else {
            cursorPosition = result.length;
        }

        return { value: result, cursor: cursorPosition };
    }

    private countDotsInRange(str: string, start: number, end: number): number {
        end = Math.min(end, str.length);
        let count = 0;
        for (let i = start; i < end; i++) {
            if (str[i] === '.') count++;
        }
        return count;
    }

    onDateDisplayInput(event: any) {
        const input = event.target as HTMLInputElement;
        const value = input.value;

        // Определяем, удаляем ли (backspace/delete)
        const previousValue = this.previousValue;
        const isDeleting = previousValue && previousValue.length > value.length;
        this.previousValue = value;

        // Применяем фильтрацию (только цифры и точки)
        const { value: formatted, cursor } = this.filterDateInput(value, input);

        // Обновляем отображаемое значение
        this.formattedDate = formatted;
        input.value = formatted;
        input.setSelectionRange(cursor, cursor);

        // === НОВАЯ ЛОГИКА: Проверяем полную дату и корректируем год ===
        const parts = formatted.split('.');
        const day = parts[0];
        const month = parts[1];
        const yearStr = parts[2];

        const currentYear = new Date().getFullYear();

        // Проверяем, введена ли полная дата (ДД.ММ.ГГГГ)
        if (
            day &&
            month &&
            yearStr &&
            day.length === 2 &&
            month.length === 2 &&
            yearStr.length === 4
        ) {
            const year = parseInt(yearStr, 10);

            // Если год больше текущего — заменяем на текущий
            if (year > currentYear) {
                const correctedDate = `${day}.${month}.${currentYear}`;
                this.formattedDate = correctedDate;
                input.value = correctedDate;

                // Обновляем курсор
                const newCursor = 6 + 4; // после точки после месяца + 4 цифры года
                input.setSelectionRange(newCursor, newCursor);

                // Устанавливаем исправленную дату в скрытое поле: YYYY-MM-DD
                const isoDate = `${currentYear}-${month}-${day}`;
                this.subform
                    .get('date')
                    ?.setValue(isoDate, { emitEvent: true });
                return; // выходим, чтобы не проверять isValidDate
            }

            // Если год корректен — проверяем валидность даты
            if (this.isValidDate(day, month, yearStr)) {
                const isoDate = `${yearStr}-${month}-${day}`;
                this.subform
                    .get('date')
                    ?.setValue(isoDate, { emitEvent: true });
            } else {
                this.subform.get('date')?.setValue('', { emitEvent: true });
            }
        } else {
            // Если дата неполная — очищаем скрытое поле
            this.subform.get('date')?.setValue('', { emitEvent: true });
        }
    }

    // Добавьте это свойство в класс компонента
    private previousValue: string = '';

    onDateKeydown(event: KeyboardEvent) {
        const input = event.target as HTMLInputElement;
        const cursorPosition = input.selectionStart || 0;
        const value = input.value;

        // Разрешаем только цифры, backspace, delete, tab, escape и enter
        if (
            !/[0-9]/.test(event.key) &&
            ![
                'Backspace',
                'Delete',
                'Tab',
                'Escape',
                'Enter',
                'ArrowLeft',
                'ArrowRight',
            ].includes(event.key)
        ) {
            // Разрешаем Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
            if (
                !(event.ctrlKey || event.metaKey) ||
                !['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())
            ) {
                event.preventDefault();
            }
        }

        // Обработка backspace для удаления точки и предыдущего символа
        if (event.key === 'Backspace' && cursorPosition > 0) {
            // Если курсор находится сразу после точки
            if (cursorPosition === 3 || cursorPosition === 6) {
                event.preventDefault();
                const newValue =
                    value.substring(0, cursorPosition - 2) +
                    value.substring(cursorPosition);
                input.value = newValue;
                input.setSelectionRange(cursorPosition - 2, cursorPosition - 2);
                this.onDateDisplayInput({ target: input });
            }
        }

        // Обработка delete для удаления точки и следующего символа
        if (event.key === 'Delete' && cursorPosition < value.length) {
            // Если курсор находится перед точкой
            if (cursorPosition === 2 || cursorPosition === 5) {
                event.preventDefault();
                const newValue =
                    value.substring(0, cursorPosition) +
                    value.substring(cursorPosition + 2);
                input.value = newValue;
                input.setSelectionRange(cursorPosition, cursorPosition);
                this.onDateDisplayInput({ target: input });
            }
        }
    }

    isValidDate(day: string, month: string, year: string): boolean {
        const d = parseInt(day, 10);
        const m = parseInt(month, 10);
        const y = parseInt(year, 10);

        // Проверка диапазонов
        if (y < 1900 || y > new Date().getFullYear()) return false;
        if (m < 1 || m > 12) return false;
        if (d < 1 || d > 31) return false;

        // Проверка через Date
        const date = new Date(y, m - 1, d);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return (
            date.getFullYear() === y &&
            date.getMonth() === m - 1 &&
            date.getDate() === d &&
            date <= today
        );
    }
    // Обновите метод formatDateDisplay для обратной совместимости
    private formatDateDisplay(dateStr: string) {
        if (!dateStr) {
            this.formattedDate = '';
            return;
        }

        const [year, month, day] = dateStr.split('-');
        if (year && month && day) {
            this.formattedDate = `${day}.${month}.${year}`;
        } else {
            this.formattedDate = '';
        }
    }
}
